import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Package, Warehouse, Target, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import RegionSummaryWidget from "@/components/RegionSummaryWidget";
import OverdueLeadsWidget from "@/components/OverdueLeadsWidget";
import PipelineDemandWidget from "@/components/PipelineDemandWidget";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";

const cards = [
  { key: "clients", label: "Clients", icon: Users, to: "/clients", color: "bg-indigo-50 text-indigo-700" },
  { key: "catalog", label: "Machine Models", icon: Package, to: "/catalog", color: "bg-amber-50 text-amber-700" },
  { key: "inventory", label: "Stock Units", icon: Warehouse, to: "/inventory", color: "bg-emerald-50 text-emerald-700" },
  { key: "leads", label: "Open Leads", icon: Target, to: "/leads", color: "bg-rose-50 text-rose-700" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ clients: 0, catalog: 0, inventory: 0, leads: 0 });
  const [stockByStatus, setStockByStatus] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const want = cards.filter((c) => can(user, c.key, "view"));
        const res = await Promise.all(want.map((c) => api.get(`/${c.key}`).catch(() => ({ data: [] }))));
        const by = Object.fromEntries(want.map((c, i) => [c.key, res[i].data]));
        if (by.inventory) setStockByStatus(by.inventory.reduce((acc, u) => ({ ...acc, [u.status]: (acc[u.status] || 0) + 1 }), {}));
        setCounts({
          clients: (by.clients || []).length,
          catalog: (by.catalog || []).length,
          inventory: (by.inventory || []).length,
          leads: (by.leads || []).filter((l) => l.stage !== "PO Received" && l.stage !== "Lost").length,
        });
      } catch (_e) {
        // ignore, show zeros
      }
    })();
  }, [JSON.stringify(user?.permissions || {})]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Overview</div>
        <h1 className="font-heading text-3xl font-semibold text-slate-900">Command center</h1>
        <p className="text-slate-500 text-sm mt-1">A quick read on your foundation data — clients, models, stock, and pipeline.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.filter((c) => can(user, c.key, "view")).map((c) => (
          <Link
            key={c.key}
            to={c.to}
            data-testid={`dash-card-${c.key}`}
            className="group bg-white border border-slate-200 rounded-lg p-5 hover:border-indigo-300 hover:shadow-sm transition-colors duration-150"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`w-10 h-10 rounded-md flex items-center justify-center ${c.color}`}>
                <c.icon className="w-5 h-5" />
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-600 transition-colors duration-150" />
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{c.label}</div>
            <div className="font-heading text-3xl font-semibold text-slate-900">{counts[c.key]}</div>
            {c.key === "inventory" && Object.keys(stockByStatus).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500" data-testid="dash-stock-breakdown">
                {Object.entries(stockByStatus).map(([st, n]) => (
                  <span key={st} className={st === "Rental" ? "text-fuchsia-700 font-medium" : ""} data-testid={`dash-stock-${st.replace(/\s+/g, "-").toLowerCase()}`}>{n} {st}</span>
                ))}
              </div>
            )}
          </Link>
        ))}
      </div>

      {can(user, "leads", "view") && (
        <>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <RegionSummaryWidget />
            </div>
            <OverdueLeadsWidget />
          </div>
          <div className="mt-6">
            <PipelineDemandWidget />
          </div>
        </>
      )}

      <div className="mt-10 bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Where to start</h2>
        <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5">
          {can(user, "catalog", "view") && <li>Add a few machine models in <Link className="text-indigo-600 hover:underline" to="/catalog">Machine Catalog</Link>.</li>}
          {can(user, "inventory", "view") && <li>Log physical units in <Link className="text-indigo-600 hover:underline" to="/inventory">Inventory</Link> — link each unit to a catalog model and give it a unique serial number.</li>}
          {can(user, "clients", "view") && <li>Create client accounts and multiple site addresses in <Link className="text-indigo-600 hover:underline" to="/clients">Clients</Link>.</li>}
          {can(user, "leads", "view") && <li>Track your pipeline on the <Link className="text-indigo-600 hover:underline" to="/leads">Leads</Link> kanban.</li>}
        </ul>
      </div>
    </div>
  );
}
